/**
 * Headcount.tsx — /admin/headcount. PlatformOwner-only editor of workforce headcount, reskinned to the
 * teal "upload" design system and mounted as an in-place modal panel over AdminHome (HeadcountPanel)
 * rather than the old full-page navy screen. Where the platform owner states how many people the
 * platform is for, which is the denominator of the headline reach figure on the manager dashboard
 * (MD-F07).
 *
 * WHY THIS PANEL HAD TO EXIST BEFORE REACH COULD EVER SHOW A NUMBER. `app.headcount` has been in the
 * schema since migration 0013 with no reader, no writer and no surface, so the workforce size was
 * SUPPLIED-BY-NOBODY and reach was permanently unavailable. It is deliberately not derived from the
 * number of accounts: the platform is for everyone in the organisation, not only the people who have
 * signed in, and dividing by the sign-in count would make reach approach 100% as adoption stalled.
 * That inversion is precisely the figure a manager would act on, so the denominator has to be stated
 * by a person who knows it.
 *
 * THE SCOPE IS A PICKER, NOT A TEXT BOX, AND THAT IS THE LOAD-BEARING DECISION HERE. `app.headcount`
 * .scope and `app.user_profile.department` are both free TEXT, and reach matches them to each other by
 * string. So a hand-typed 'Aftersales ' or 'aftersales' saves happily, is then read by nothing, and
 * that department's reach card goes on saying "headcount not recorded" with no hint anywhere that a
 * figure WAS entered - the worst available outcome, because it looks like the entry was lost. The
 * server refuses an unknown scope for that reason; offering only the scopes it accepts means nobody
 * has to discover the rule by being refused. The list comes from the same route that validates it.
 *
 * The figures are visibly DATED. Reach divides by whatever was last recorded, so a manager reading a
 * share of the workforce is entitled to know the workforce was last counted in March.
 *
 * The panel fetches its own data on open via useOverlayData (AdminHome does not preload it) and, after
 * a save, calls reload() to refresh in place instead of a router revalidation. The still-registered
 * /admin/headcount route renders through a thin Headcount() wrapper that opens the same panel over a
 * blank backdrop for the deep-link path.
 *
 * Provisional engineering UI — matches the other admin governance panels' own posture.
 */
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { apiGet, apiPut, type HeadcountPayload, type HeadcountRow } from './api';
import { shortWhen } from './shared';
import {
  EmptyState,
  ErrorLine,
  KombiAdminOverlay,
  NotPermitted,
  Notice,
  PrimaryAction,
  ProvisionalNote,
  SectionHeader,
  Spinner,
} from '../kombi/admin/chrome';
import { useOverlayData, type ReturnFocusRef } from './overlay';

export type HeadcountLoaderData =
  | { state: 'signedOut' }
  | { state: 'forbidden' }
  | { state: 'ok'; headcounts: HeadcountRow[]; scopes: string[] };

export async function headcountLoader(): Promise<HeadcountLoaderData> {
  const res = await apiGet<HeadcountPayload>('/api/admin/headcount');
  if (res.kind === 'unauthorized') return { state: 'signedOut' };
  if (res.kind === 'error') {
    if (res.status === 403) return { state: 'forbidden' };
    throw new Error(`GET /api/admin/headcount failed (${res.status})`);
  }
  return { state: 'ok', headcounts: res.body.headcounts, scopes: res.body.scopes };
}

/** The platform-wide scope, spelled exactly as the server's own `ALL_SCOPE`. */
const ALL_SCOPE = 'ALL';

/**
 * What a scope MEANS, in the reader's terms rather than the column's.
 *
 * 'ALL' is not a department and reads like a placeholder if left bare, yet it is the one figure the
 * headline reach card divides by, so it is worth naming plainly.
 */
function describeScope(scope: string): string {
  return scope === ALL_SCOPE ? 'Everyone the platform is for' : `People in ${scope}`;
}

/** Turn the server's named refusals into a sentence that says what to do about it. */
function refusalMessage(error: string): string {
  switch (error) {
    case 'unknown_scope':
      return 'That scope is not one the reach figure can match, so nothing was recorded. Choose one from the list.';
    case 'invalid_headcount':
      return 'A headcount has to be a whole number above zero.';
    case 'invalid_request':
      return 'A headcount has to be a whole number above zero.';
    default:
      return `Could not record that (${error}).`;
  }
}

/**
 * The recording form. One control for the whole panel rather than one per row, because recording a
 * headcount is a REPLACEMENT keyed by scope (the route is a PUT) - so the same form both creates the
 * first figure for a department and revises it, and there is no second thing a per-row editor could do.
 *
 * The scope defaults to whichever scope has NOT been recorded yet, if any. On a fresh platform that is
 * 'ALL', which is the figure the headline reach card needs and therefore the one worth landing on.
 */
function RecordForm({
  scopes,
  recorded,
  onSaved,
}: {
  scopes: string[];
  /** Scopes that already have a figure - used only to choose a sensible default, never to restrict. */
  recorded: Set<string>;
  onSaved: () => void;
}) {
  const firstUnrecorded = scopes.find((s) => !recorded.has(s));
  const [scope, setScope] = useState(firstUnrecorded ?? scopes[0] ?? ALL_SCOPE);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  /** A refusal reads as an error (red ErrorLine); the happy "Recorded." line stays muted. */
  const [isError, setIsError] = useState(false);

  async function save() {
    // Parsed here only to say "that is not a number" without a round trip; the column's own
    // CHECK (headcount > 0) and the route's schema remain the authority.
    const headcount = Number.parseInt(text.trim(), 10);
    if (!Number.isInteger(headcount) || headcount <= 0) {
      setMessage('A headcount has to be a whole number above zero.');
      setIsError(true);
      return;
    }
    setBusy(true);
    setMessage(null);
    setIsError(false);
    try {
      const res = await apiPut<HeadcountPayload>(
        `/api/admin/headcount/${encodeURIComponent(scope)}`,
        { headcount },
      );
      if (res.kind === 'ok') {
        setText('');
        setMessage('Recorded.');
        setIsError(false);
        onSaved();
        return;
      }
      setMessage(
        res.kind === 'unauthorized'
          ? 'Your session has expired. Sign in again.'
          : refusalMessage(res.code ?? `HTTP ${res.status}`),
      );
      setIsError(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
      className="kombi-dialog-stack"
    >
      <label className="kombi-field">
        <span>Who is being counted</span>
        <select onChange={(e) => setScope(e.target.value)} value={scope}>
          {scopes.map((s) => (
            <option key={s} value={s}>
              {s === ALL_SCOPE ? 'Everyone (whole platform)' : s}
            </option>
          ))}
        </select>
        <span className="learn-note">
          Only scopes the reach figure can match are offered. A department appears here as soon as
          it exists, including one that has been retired — people recorded against it are still
          counted.
        </span>
      </label>

      <label className="kombi-field">
        <span>How many people</span>
        <input
          type="number"
          min={1}
          step={1}
          inputMode="numeric"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <span className="learn-note">
          The number of people the platform is for in that scope — not the number who have signed
          in. Recording a figure replaces the previous one for that scope; the previous value is
          kept in the governance trail.
        </span>
      </label>

      <div>
        <PrimaryAction submit>{busy ? 'Recording…' : 'Record'}</PrimaryAction>
      </div>

      {message &&
        (isError ? (
          <ErrorLine>{message}</ErrorLine>
        ) : (
          <p role="status" className="learn-note">
            {message}
          </p>
        ))}
    </form>
  );
}

/**
 * The overlay body AdminHome mounts. Fetches its own data on open (useOverlayData → headcountLoader),
 * shows the pending line while loading, an ErrorLine for a failed load / ended session, NotPermitted
 * for a forbidden result, and the recording form + recorded list on success — a save reloads() in place.
 */
export function HeadcountPanel({
  onClose,
  returnFocusRef: _returnFocusRef,
}: {
  onClose: () => void;
  returnFocusRef: ReturnFocusRef;
}) {
  const { data, loading, error, reload } = useOverlayData(headcountLoader);

  return (
    <KombiAdminOverlay title="Headcount" onClose={onClose}>
      {loading ? (
        <Spinner label="Loading headcount…" />
      ) : error || (data && data.state === 'signedOut') ? (
        // signedOut inside an overlay: don't navigate — the parent grid owns auth. Just say so.
        <ErrorLine>
          Your session has ended or the headcount could not be loaded. Close this panel and sign in
          again.
        </ErrorLine>
      ) : data && data.state === 'forbidden' ? (
        <NotPermitted what="Headcount" />
      ) : data && data.state === 'ok' ? (
        <HeadcountBody scopes={data.scopes} headcounts={data.headcounts} onSaved={reload} />
      ) : null}
    </KombiAdminOverlay>
  );
}

/** The ok-state body: the intro notice, the recording form, and the recorded-so-far list. */
function HeadcountBody({
  scopes,
  headcounts,
  onSaved,
}: {
  scopes: string[];
  headcounts: HeadcountRow[];
  onSaved: () => void;
}) {
  const recorded = new Set(headcounts.map((h) => h.scope));
  // Every offerable scope, whether or not a figure exists for it, so an unrecorded department is
  // VISIBLE as unrecorded. A list of only what has been recorded reads as complete, which is exactly
  // the wrong impression on the panel whose whole job is to notice the gaps.
  const rows = scopes.map((scope) => ({
    scope,
    figure: headcounts.find((h) => h.scope === scope) ?? null,
  }));

  return (
    <div>
      <ProvisionalNote />
      <Notice>
        How many people the platform is for (MD-F04). The manager dashboard divides by these figures
        to show reach as a share of the workforce, so until one is recorded that card says so rather
        than guessing. It is not the number of accounts: counting only the people who have signed in
        would make reach approach 100% as adoption stalled.
      </Notice>

      <SectionHeader>Record a headcount</SectionHeader>
      <div className="kombi-admin-card">
        <RecordForm scopes={scopes} recorded={recorded} onSaved={onSaved} />
      </div>

      <SectionHeader>Recorded headcounts</SectionHeader>
      {rows.length === 0 ? (
        <EmptyState>
          No scopes yet. A department becomes recordable as soon as it exists.
        </EmptyState>
      ) : (
        <ul className="kombi-dialog-stack">
          {rows.map(({ scope, figure }) => (
            <li key={scope} className="kombi-admin-card">
              <div>
                {scope === ALL_SCOPE ? 'Everyone (whole platform)' : scope}
                {figure ? ` — ${figure.headcount.toLocaleString()}` : ''}
              </div>
              <p className="learn-note">
                {figure
                  ? `${describeScope(scope)} · recorded ${shortWhen(figure.recordedAt)}`
                  : `${describeScope(scope)} · not recorded, so reach for this scope is unavailable`}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Thin route wrapper for the still-registered /admin/headcount deep link. Opens the panel always-open
 * over a blank backdrop; the panel still fetches via useOverlayData, and Close returns to /admin.
 */
export function Headcount() {
  const navigate = useNavigate();
  const ref = useRef<HTMLElement | null>(null);
  return <HeadcountPanel onClose={() => navigate('/admin')} returnFocusRef={ref} />;
}
